import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";

/**
 * Hybrid daily stock ledger.
 * Today's row is auto-computed (opening + in − out − consumption = closing) from movements.
 * Store / Warehouse Manager taps "Verify & lock" to freeze closing for the day.
 * History rows are read-only.
 */
export default function StockDailyLedger({ category, title, subtitle, ownerLabel, testid }) {
  const [today, setToday] = useState([]);
  const [history, setHistory] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [verifyRow, setVerifyRow] = useState(null);
  const [closingOverride, setClosingOverride] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    const { data } = await api.get(`/org/stock-daily?category=${category}&days=14`);
    setToday(data.today || []);
    setHistory(data.history || []);
    setTodayDate(data.today_date || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const openVerify = (r) => {
    setVerifyRow(r);
    setClosingOverride(String(r.closing));
    setNote("");
  };
  const submitVerify = async () => {
    try {
      await api.post("/org/stock-daily/verify", {
        item_id: verifyRow.item_id,
        closing_override: Number(closingOverride),
        note: note || null,
      });
      toast.success("Locked for today");
      setVerifyRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // group history by date for display
  const byDate = {};
  history.forEach((r) => {
    byDate[r.date] = byDate[r.date] || [];
    byDate[r.date].push(r);
  });
  const dates = Object.keys(byDate).sort().reverse();

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid={testid}>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">{title}</h1>
        <p className="text-muted-foreground">{subtitle}</p>
        <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mt-2">Maintained by · {ownerLabel}</div>
      </div>

      <Tabs defaultValue="today">
        <TabsList>
          <TabsTrigger value="today" data-testid="tab-today">Today ({todayDate})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History · 14 days</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-3">
          <div className="surface-card p-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU · Item</TableHead>
                  <TableHead>Opening</TableHead>
                  <TableHead>IN</TableHead>
                  <TableHead>OUT</TableHead>
                  <TableHead>Consumption</TableHead>
                  <TableHead>Closing</TableHead>
                  <TableHead>Lock</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {today.map((r) => (
                  <TableRow key={r.item_id} data-testid={`stockdaily-row-${r.item_sku}`}>
                    <TableCell>
                      <div className="text-sm font-medium">{r.item_name}</div>
                      <div className="text-xs font-mono text-muted-foreground">{r.item_sku} · {r.unit}</div>
                    </TableCell>
                    <TableCell className="font-mono">{r.opening}</TableCell>
                    <TableCell className="font-mono text-emerald-700">+{r.in}</TableCell>
                    <TableCell className="font-mono text-red-700">−{r.out}</TableCell>
                    <TableCell className="font-mono text-amber-700">−{r.consumption}</TableCell>
                    <TableCell className="font-mono font-bold">{r.closing}</TableCell>
                    <TableCell>
                      {r.locked ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-900 border-emerald-200 font-mono text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1 inline" /> LOCKED
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => openVerify(r)} data-testid={`stockdaily-verify-${r.item_sku}`}>
                          <Lock className="w-3 h-3 mr-1" /> Verify & lock
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {today.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No items in this bucket.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          {dates.length === 0 ? (
            <div className="surface-card p-8 text-center text-muted-foreground">No locked snapshots yet — verify today&apos;s row to start the history.</div>
          ) : null}
          {dates.map((d) => (
            <div key={d} className="surface-card p-4 overflow-x-auto" data-testid={`stockdaily-history-${d}`}>
              <h3 className="font-display text-sm font-black mb-3">{d}</h3>
              <Table>
                <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Opening</TableHead><TableHead>IN</TableHead><TableHead>OUT</TableHead><TableHead>Consumption</TableHead><TableHead>Closing</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
                <TableBody>
                  {byDate[d].map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm">{r.item_sku} · {r.item_name}</TableCell>
                      <TableCell className="font-mono">{r.opening}</TableCell>
                      <TableCell className="font-mono text-emerald-700">+{r.in}</TableCell>
                      <TableCell className="font-mono text-red-700">−{r.out}</TableCell>
                      <TableCell className="font-mono text-amber-700">−{r.consumption}</TableCell>
                      <TableCell className="font-mono font-bold">{r.closing}</TableCell>
                      <TableCell className="text-xs">{r.locked_by || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!verifyRow} onOpenChange={(v) => !v && setVerifyRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Verify & lock — {verifyRow?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Auto-computed closing: <b>{verifyRow?.closing}</b> ({verifyRow?.opening} + {verifyRow?.in} − {verifyRow?.out} − {verifyRow?.consumption})
            </div>
            <div className="space-y-2"><Label>Closing (override if needed)</Label><Input type="number" value={closingOverride} onChange={(e) => setClosingOverride(e.target.value)} data-testid="stockdaily-closing-input" /></div>
            <div className="space-y-2"><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Discrepancy reason if any…" /></div>
            <Button onClick={submitVerify} data-testid="stockdaily-verify-submit">Lock day</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
